import React, { useCallback, useEffect, useState } from 'react';
import { apiService } from '../../../services/api';
import { useToast } from '../../../contexts/ToastContext';
import StatusPill from '../../flow/StatusPill';
import { FLOW_ROLES, FlowRole, roleLabel } from '../../flow/types';
import RoleToggle from './RoleToggle';

export interface StatusRow {
  status: string;
  position: number | null;
  owner_roles: string[];
  is_terminal: boolean;
  is_bounce: boolean;
  stuck_after_days: number | null;
  note: string | null;
  aliases: string[];
}

const CARD = 'rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400';

const typeLabel = (t: string) => (t === 'devops' ? 'DevOps' : t.charAt(0).toUpperCase() + t.slice(1));

function StatusEditor({ row, onSaved }: { row: StatusRow; onSaved: () => void }) {
  const { showToast } = useToast();
  const [saving, setSaving] = useState(false);
  const [days, setDays] = useState(row.stuck_after_days === null ? '' : String(row.stuck_after_days));
  useEffect(() => setDays(row.stuck_after_days === null ? '' : String(row.stuck_after_days)), [row.stuck_after_days]);

  const anyone = row.owner_roles.includes('any');

  const save = async (body: { owner_roles?: string[]; stuck_after_days?: number | null }) => {
    setSaving(true);
    try {
      const r = await apiService.updateFlowStatus(row.status, body);
      if (r.success) onSaved();
      else showToast('error', r.error || 'Could not save');
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || 'Could not save');
      setDays(row.stuck_after_days === null ? '' : String(row.stuck_after_days));
    } finally {
      setSaving(false);
    }
  };

  const toggleRole = (role: FlowRole) => {
    const base = anyone ? [] : row.owner_roles;
    const next = base.includes(role) ? base.filter((r) => r !== role) : [...base, role];
    if (next.length === 0) {
      showToast('error', `${row.status} needs at least one owner, or choose Anyone`);
      return;
    }
    void save({ owner_roles: next });
  };

  const commitDays = () => {
    const trimmed = days.trim();
    const value = trimmed === '' ? null : Number(trimmed);
    if (value !== null && (!Number.isInteger(value) || value < 1 || value > 3650)) {
      showToast('error', 'Use a whole number of days from 1 to 3650, or leave it empty');
      setDays(row.stuck_after_days === null ? '' : String(row.stuck_after_days));
      return;
    }
    if (value !== row.stuck_after_days) void save({ stuck_after_days: value });
  };

  return (
    <li className="grid gap-x-4 gap-y-2.5 px-5 py-4 lg:grid-cols-[13rem_minmax(0,1fr)_9rem] lg:items-center">
      <div>
        <StatusPill status={row.status} />
        <div className="mt-1 text-xs text-gray-500">
          {row.note}
          {row.is_terminal ? ' · finished' : ''}
          {row.is_bounce ? ' · counts as sent back' : ''}
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5" role="group" aria-label={`Who may move a ticket into ${row.status}`}>
        {FLOW_ROLES.map((r) => (
          <RoleToggle key={r.key} label={r.label} tone={r.tone} on={!anyone && row.owner_roles.includes(r.key)} disabled={saving} onClick={() => toggleRole(r.key)} />
        ))}
        <RoleToggle label="Anyone" tone="bg-gray-100 text-gray-800 ring-gray-300" on={anyone} disabled={saving} title="A shared status: nobody is judged" onClick={() => void save({ owner_roles: anyone ? ['pm'] : ['any'] })} />
      </div>
      <label className="flex items-center gap-2 text-xs text-gray-500">
        <span className="sr-only">Days before it counts as not moved, for {row.status}</span>
        <input
          inputMode="numeric"
          value={days}
          disabled={saving}
          onChange={(e) => setDays(e.target.value.replace(/[^0-9]/g, ''))}
          onBlur={commitDays}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
          placeholder="Not tracked"
          className="w-28 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm tabular-nums text-gray-900 placeholder:text-gray-500 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
        />
        days
      </label>
    </li>
  );
}

// Who may move a ticket INTO each status, and how long an open ticket may sit there.
export default function StagesOwners() {
  const [rows, setRows] = useState<StatusRow[]>([]);
  const [executors, setExecutors] = useState<{ issue_type: string; role: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await apiService.getFlowStatuses();
      if (r.success) {
        setRows(r.data.statuses ?? []);
        setExecutors(r.data.executors ?? []);
        setError(null);
      } else setError(r.error || 'Could not load statuses');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not load statuses');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  return (
    <section className={CARD} aria-label="Stages and owners">
      <div className="border-b border-gray-100 p-5">
        <h3 className="text-sm font-semibold text-gray-900">Stages and owners</h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-600">
          A ticket should be moved into a status by the function that owns it. A move by anyone else is shown as outside the usual owner. Choose Anyone for a shared status.
          The number is how many days an open ticket may sit in a status before it is listed as not moved for a while; leave it empty to stop tracking.
        </p>
        {executors.length > 0 && (
          <p className="mt-2 text-xs text-gray-500">
            The engineering statuses also accept the team that does the hands-on work for a ticket type: {executors.map((e) => `${roleLabel(e.role)} for ${typeLabel(e.issue_type)} tickets`).join(', ')}.
          </p>
        )}
      </div>
      {error && <div className="m-5 rounded-xl bg-rose-50 px-4 py-2.5 text-sm text-rose-700 ring-1 ring-rose-200" role="alert">{error}</div>}
      {loading ? (
        <div className="divide-y divide-gray-100" role="status" aria-label="Loading statuses">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-16 animate-pulse bg-gray-50/60 motion-reduce:animate-none" />)}
        </div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {rows.map((r) => <StatusEditor key={r.status} row={r} onSaved={() => void load()} />)}
        </ul>
      )}
    </section>
  );
}
